import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../config/api';
import './TrackOrder.css';

const TrackOrder = () => {
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fetchOrder = async (orderNum, orderEmail) => {
    if (!orderNum || !orderEmail) {
      return;
    }
    
    setError('');
    setOrder(null);
    setLoading(true);
    
    try {
      const response = await api.get(`/orders/by-number/${orderNum}?email=${encodeURIComponent(orderEmail)}`);
      setOrder(response.data.order);
    } catch (error) {
      setError(error.response?.data?.error || 'Order not found. Please check your order number and email.');
    } finally {
      setLoading(false);
    }
  };
  
  // Auto-fetch order if order number and email are in URL params
  useEffect(() => {
    const urlOrderNumber = searchParams.get('orderNumber');
    const urlEmail = searchParams.get('email');
    
    if (urlOrderNumber && urlEmail) {
      setOrderNumber(urlOrderNumber);
      setEmail(urlEmail);
      // Auto-fetch the order
      fetchOrder(urlOrderNumber, urlEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetchOrder(orderNumber, email);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      processing: '#2196f3',
      shipped: '#9c27b0',
      delivered: '#4caf50',
      cancelled: '#f44336'
    };
    return colors[status] || '#666';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      paid: '#4caf50',
      failed: '#f44336',
      refunded: '#9e9e9e'
    };
    return colors[status] || '#666';
  };

  return (
    <>
      <Helmet>
        <title>Track Your Order - NieHairLabel</title>
        <meta name="description" content="Track your order status using your order number and email" />
      </Helmet>
      <div className="track-order-page">
        <div className="container">
          <h1>Track Your Order</h1>
          
          <form onSubmit={handleSubmit} className="track-order-form">
            <div className="form-group">
              <label>Order Number *</label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g., NHL-1767697127240-C70N7EET2"
                required
              />
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter the email used for this order"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Tracking...' : 'Track Order'}
            </button>
          </form>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {order && (
            <div className="order-details">
              <div className="order-header">
                <h2>Order #{order.order_number}</h2>
                <p className="order-date">Placed on {new Date(order.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</p>
              </div>

              <div className="status-cards">
                <div className="status-card">
                  <h3>Order Status</h3>
                  <div className="status-badge" style={{ backgroundColor: getStatusColor(order.order_status) }}>
                    {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                  </div>
                </div>
                <div className="status-card">
                  <h3>Payment Status</h3>
                  <div className="status-badge" style={{ backgroundColor: getPaymentStatusColor(order.payment_status) }}>
                    {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
                  </div>
                </div>
              </div>

              <div className="order-info-section">
                <h3>Customer Information</h3>
                <p><strong>Name:</strong> {order.customer_name}</p>
                <p><strong>Email:</strong> {order.customer_email}</p>
                {order.customer_phone && <p><strong>Phone:</strong> {order.customer_phone}</p>}
              </div>

              <div className="order-info-section">
                <h3>Shipping Address</h3>
                <p>{order.shipping_address}</p>
              </div>

              <div className="order-info-section">
                <h3>Order Items</h3>
                <table className="order-items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Price</th>
                      <th>Quantity</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items && order.items.map(item => (
                      <tr key={item.id}>
                        <td>{item.product_name}</td>
                        <td>${parseFloat(item.product_price || 0).toFixed(2)}</td>
                        <td>{item.quantity}</td>
                        <td>${parseFloat(item.subtotal || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>${parseFloat(order.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>${parseFloat(order.shipping_cost || 0).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Tax:</span>
                  <span>${parseFloat(order.tax || 0).toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>${parseFloat(order.total || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="order-notes">
                <p><strong>Payment Method:</strong> {order.payment_method || 'Card'}</p>
                {order.notes && (
                  <div>
                    <strong>Notes:</strong>
                    <p>{order.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TrackOrder;



